/**
 * @typedef {{
 * 		post_code: string;
 * 		city: string | null;
 * 		street: string | null;
 * 		house_number: string | null;
 * 	}} LocationData
 *
 * @typedef {{
 *     type: 'city' | 'street' | 'house_number';
 *     payload: {
 *         signal: AbortSignal,
 *         queryObject: LocationData,
 *     };
 * }} AutocompleteAction
 */

class AutocompleteField {
	/**
	 * @param {HTMLInputElement} field
	 */
	constructor(field) {
		this.field = field;
		this.fieldset = getFieldset(field);
		this.autocompleteInstance = null;
		/**
		 * @type {{isEnabled: boolean}}
		 */
		const state = { isEnabled: true };
		this.state = new Proxy(state, {
			set: (target, property, newValue) => {
				target[property] = newValue;

				if (property === 'isEnabled') {
					this.fieldset.disabled = !Boolean(newValue);
				}

				return true;
			}
		});
	}

	getElement() {
		return this.field;
	}

	// getFieldset() {
	// 	return this.fieldset;
	// }

	getAutocompleteInstance() {
		return this.autocompleteInstance;
	}

	setAutocompleteInstance(instance) {
		this.autocompleteInstance = instance;
	}

	enable() {
		this.state.isEnabled = true;
	}

	disable() {
		this.state.isEnabled = false;
	}

	/**
	 * @param {{
	 *     triggerEvent: boolean
	 * }} options
	 *
	 * @return void;
	 */
	clearValue(options = {}) {
		const { triggerEvent = false } = options;

		this.field.value = '';

		if (triggerEvent) {
			this.field.dispatchEvent(new Event('input'));
		}
	}
}

class AutocompleteGroup {
	constructor(rootField, form) {
		this.form = form;
		this.formId = form.id;
		this.rootField = rootField;
		/**
		 * @type {AutocompleteField[]}
		 */
		this.fields = [];
		this.lastSlicedArrayLength = -1;

		try {
			const state = this.initState();
			this.state = new Proxy(state, {
				get: (target, property) => {
					return target[property];
				},
				set: (target, property, newValue) => {
					target[property] = newValue;
					if (property === 'autocompleteData') {
						this.updateFieldsDisability();
					}
					return true;
				}
			});
		} catch (error) {
			console.error('state error', error);
			return;
		}

		this.abortController = new AbortController();
		this.initFields();
		this.updateFieldsDisability();
	}

	getQueryString() {
		return Object.values(this.state.queryObject).filter(Boolean).join(' ');
	}

	abortFetchRequest() {
		this.abortController.abort();
		this.abortController = new AbortController();
	}

	async fetchData(type) {
		try {
			const query = this.getQueryString();
			const token = await request_captcha('location_info');

			const {
				success,
				data
			} = await fetch('/wp-json/contract-form/get-location-info', {
				method: 'POST',
				signal: this.abortController.signal,
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					token,
					query,
					type,
				}),
			}).then((response) => response.json());

			if (!success) {
				throw new Error('success in response is false');
			}

			return data;
		} catch (error) {
			console.log(error.name)
			if (error.name !== 'AbortError') {
				console.error('Fetch data error occured', error)
			}
			return [];
		}
	}

	/**
	 * @return {{queryObject: object, autocompleteData: object}}
	 */
	initState() {
		const rootFieldType = this.rootField.dataset?.autocompleteRoot ?? '';
		const queryObject = { [rootFieldType]: null };
		const autocompleteData = { [rootFieldType]: [] };
		const fields = this.form.querySelectorAll(`[data-autocomplete-group-root="${this.rootField.name}"]`)
		for (const fieldElement of fields) {
			const type = fieldElement.dataset?.autocompleteType ?? '';
			if (!type) {
				continue;
			}

			queryObject[type] = null;
			autocompleteData[type] = null;
		}

		return {
			queryObject,
			autocompleteData,
		};
	}

	initFields() {
		const groupInstance = this;

		// Init root field
		const rootType = groupInstance.rootField.dataset?.autocompleteRoot ?? '';
		if (!rootType) {
			console.error('no root type', groupInstance.rootField);
			return;
		}

		groupInstance.rootField.addEventListener('input', debounce(() => {
			removeMessage(groupInstance.rootField);
			const isValid = validateField(groupInstance.rootField);
			// if (!isValid) return;

			const newObject = Object.keys(groupInstance.state.queryObject).reduce((accumulator, current) => ({
				...accumulator,
				[current]: null
			}), {});
			newObject[rootType] = isValid ? groupInstance.rootField.value : null;
			groupInstance.updateQueryObject(newObject);
			groupInstance.updateAutocompleteData(newObject);
			groupInstance.updateFieldsDisability({ clearLastEnabled: true });
		}, 500));


		// Init other fields
		const fields = this.form.querySelectorAll(`[data-autocomplete-group-root="${this.rootField.name}"]`)
		for (const _fieldElement of fields) {
			const field = new AutocompleteField(_fieldElement);
			const fieldElement = field.getElement();
			const fieldId = fieldElement.id;

			if (fieldElement.classList.contains('input--autocompletefield')) {
				const type = fieldElement.dataset?.autocompleteType ?? '';
				if (!type) {
					console.error('no type')
					return;
				}

				const fetchAutocompleteData = async (inputValue) => {
					const loadingElement = document.createElement('li');
					loadingElement.className = 'loader-wrapper';
					loadingElement.innerHTML = `<span class="loader"></span>`;

					const list = instance.list;

					list.classList.add('loading');
					if (!list.querySelector('.loader')) {
						list.innerHTML = '';
						list.prepend(loadingElement);
						instance.open()
					}

					try {
						groupInstance.abortFetchRequest();
						groupInstance.updateQueryObject({ [type]: inputValue.trim() })
						const data = await groupInstance.fetchData(type);
						this.updateAutocompleteData({ [type]: data });
						return data;
					} catch (error) {
						return error;
					} finally {
						list.classList.remove('loading');
					}
				}

				const validateQuery = (query, instance) => {
					return query.trim().length >= instance.threshold;
				}

				const renderListElement = (list, data) => {
					if (!data.results.length) {
						// Create "No Results" message list element
						const message = document.createElement("li");
						message.setAttribute("class", "no_result");
						// Add message text content
						message.innerHTML = `<span>Nie znaleziono wyników${data.query.length ? ` dla "${data.query}"` : ''}</span>`;
						// Add message list element to the list
						list.appendChild(message);
					}
				}

				// Event handlers
				const handleInput = () => {
					groupInstance.triggerChange(field);
				}
				const handleSelection = (event) => {
					const input = event.currentTarget;
					const listItem = event.detail.event.target;
					const { selection } = event.detail;
					if (listItem.classList.contains('no_result') || listItem.classList.contains('loader-wrapper')) {
						return;
					}

					if (selection?.match) {
						if (input.value !== selection.match) {
							input.value = selection.match;
							groupInstance.triggerChange(field);
							groupInstance.updateQueryObject({ [type]: selection.match })
							groupInstance.updateFieldsDisability({ clearLastEnabled: true });
						}
					}
				}

				const handleFocus = (event, instance) => {
					const input = event.currentTarget;
					const feedback = instance?.feedback;
					if (input.value.trim().length) {
						if (feedback) {
							instance.open();
						}
					} else {
						if (feedback && feedback?.query === input.value) {
							instance.open()
						} else {
							instance.start();
						}
					}
				}

				const instance = new window.autoComplete({
					name: fieldId,
					selector: () => fieldElement,
					diacritics: true,
					debounce: 500,
					threshold: 0,
					data: {
						src: fetchAutocompleteData,
						keys: [type]
					},
					trigger: (query) => validateQuery(query, instance),
					resultsList: {
						class: 'autocomplete-list',
						noResults: true,
						maxResults: 20,
						element: renderListElement,
					},
				});

				instance.input.addEventListener('input', (event) => handleInput(event, instance));
				instance.input.addEventListener('selection', (event) => handleSelection(event, instance));
				instance.input.addEventListener('focus', (event) => handleFocus(event, instance));

				field.setAutocompleteInstance(instance);
			}

			this.addField(field);
		}
	}

	triggerChange(field) {
		const fieldElement = field.getElement();
		const { autocompleteRoot = '', autocompleteType = '' } = fieldElement.dataset;
		const type = autocompleteType || autocompleteRoot || '';
		const keys = Object.keys(this.state.queryObject);
		const currentFieldIndex = keys.findIndex((key) => key === type);

		const slicedArray = keys.slice(currentFieldIndex + 1);
		this.lastSlicedArrayLength = slicedArray.length;
		const clearLastEnabled = slicedArray.length > this.lastSlicedArrayLength;
		const newQueryObject = Object.fromEntries(slicedArray.map((entry) => ([entry, null])));
		this.updateQueryObject(newQueryObject);
		this.updateFieldsDisability({ clearLastEnabled });
	}

	/**
	 * @param {AutocompleteField} field
	 */
	addField(field) {
		this.fields = [...this.fields, field];
	}

	/**
	 * @param {AutocompleteField|null} field
	 * @returns {{shouldDisable: AutocompleteField[], shouldEnable: AutocompleteField[]}}
	 */
	checkFieldsDisability(field = null) {
		const status = {
			shouldEnable: [],
			shouldDisable: [],
		};

		/**
		 * @param {boolean} where
		 * @param {AutocompleteField} what
		 */
		const setStatus = (where, what) => {
			status[where ? 'shouldEnable' : 'shouldDisable'].push(what);
		}

		const fieldsToCheck = field ? [field] : this.fields;

		for (const field of fieldsToCheck) {
			const fieldElement = field.getElement();
			const { disabledUntilAutocompleted = '' } = fieldElement.dataset;
			if (!disabledUntilAutocompleted) {
				console.error('no disabled until autocompleted');
				setStatus(true, field);
				continue;
			}

			const dependTarget = this.form.querySelector(`[name="${disabledUntilAutocompleted}"]`);

			const { autocompleteType = '', autocompleteRoot = '' } = dependTarget.dataset;
			const dependTargetType = autocompleteType || autocompleteRoot || null;
			if (!(dependTargetType || !this.state.queryObject?.[dependTargetType])) {
				console.error('depend target has invalid type', {
					dependTargetType,
					autocompleteType,
					autocompleteRoot,
				});

				setStatus(true, field);
				continue;
			}


			const queryValue = this.state.queryObject[dependTargetType];
			setStatus(!!queryValue, field);
		}

		return status;
	}

	/**
	 * @param {{
	 *     clearLastEnabled: boolean;
	 * }} options
	 * @returns void
	 */
	updateFieldsDisability(options = {}) {
		const {
			clearLastEnabled = false,
		} = options;

		const checkedFields = this.checkFieldsDisability();

		for (const field of checkedFields.shouldEnable) {
			field.enable();
		}
		const last = checkedFields.shouldEnable.length - 1;
		if (clearLastEnabled && last >= 0) {
			const lastField = checkedFields.shouldEnable[last]
			lastField.clearValue();
			const autocompleteInstance = lastField.getAutocompleteInstance();
			if (autocompleteInstance) {
				delete autocompleteInstance.feedback;
			}
		}

		for (const field of checkedFields.shouldDisable) {
			field.disable();
			field.clearValue();
			const autocompleteInstance = field.getAutocompleteInstance();
			if (autocompleteInstance) {
				delete autocompleteInstance.feedback;
			}
		}
	}

	updateQueryObject(newQueryObject) {
		this.state.queryObject = { ...this.state.queryObject, ...newQueryObject };
	}

	updateAutocompleteData(newAutocompleteData) {
		this.state.autocompleteData = { ...this.state.autocompleteData, ...newAutocompleteData };
	}
}
